import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatCurrency } from '../../../utils/currency';
import { payoffPlanService } from '../../../services/payoffPlanService';


const WhatIfAnalysis = ({ debts, portfolioSummary, isPro }) => {
  const [scenario, setScenario] = useState({
    extraMonthlyPayment: 0,
    oneTimePayment: 0,
    extraRecurringPayment: 0,
    strategy: 'avalanche',
    focusedDebtId: null,
    targetPayoffDate: null,
    multiDebtPayments: {}
  });

  const [currentPlan, setCurrentPlan] = useState(null);
  const [optimizedPlan, setOptimizedPlan] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [focusedDebtPlan, setFocusedDebtPlan] = useState(null);
  const [targetDateAnalysis, setTargetDateAnalysis] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [showSavePlanModal, setShowSavePlanModal] = useState(false);
  const [planName, setPlanName] = useState('');
  const [savingPlan, setSavingPlan] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showMultiDebtMode, setShowMultiDebtMode] = useState(false);

  useEffect(() => {
    if (debts?.length > 0) {
      calculatePlans();
    }
  }, [debts, scenario]);

  const handleSavePlan = async () => {
    if (!planName?.trim()) {
      setSaveError('Please enter a plan name');
      return;
    }

    if (!scenario?.focusedDebtId && !showMultiDebtMode) {
      setSaveError('Please select a debt to focus on');
      return;
    }

    setSavingPlan(true);
    setSaveError('');

    try {
      // Convert payoffDate string to proper date format for database
      const payoffDateForDB = optimizedPlan?.payoffDateObj 
        ? optimizedPlan?.payoffDateObj?.toISOString()?.split('T')?.[0] 
        : null;

      const planData = {
        debtId: scenario?.focusedDebtId || debts?.[0]?.id,
        planName: planName,
        strategy: scenario?.strategy || 'focused',
        extraMonthlyPayment: parseFloat(scenario?.extraMonthlyPayment || 0),
        oneTimePayment: parseFloat(scenario?.oneTimePayment || 0),
        extraRecurringPayment: parseFloat(scenario?.extraRecurringPayment || 0),
        focusedDebtId: scenario?.focusedDebtId,
        targetPayoffDate: scenario?.targetPayoffDate || null,
        monthsToPayoff: optimizedPlan?.months,
        totalInterest: optimizedPlan?.totalInterest,
        interestSaved: currentPlan?.totalInterest - optimizedPlan?.totalInterest,
        timeSavedMonths: currentPlan?.months - optimizedPlan?.months,
        projectedPayoffDate: payoffDateForDB,
        planDetails: {
          scenario: scenario,
          multiDebtPayments: scenario?.multiDebtPayments
        }
      };

      await payoffPlanService?.create(planData);
      setShowSavePlanModal(false);
      setPlanName('');
      setSaveError('');
    } catch (err) {
      setSaveError(err?.message || 'Failed to save plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const calculatePlans = () => {
    const activeDebts = debts?.filter(d => d?.status === 'active') || [];
    if (activeDebts?.length === 0) return;

    // Calculate current plan (minimum payments only)
    const current = calculatePayoffPlan(activeDebts, 0, 'current');
    setCurrentPlan(current);

    // Calculate optimized plan with what-if scenario
    let totalExtraPayment = parseFloat(scenario?.extraMonthlyPayment || 0) + 
                             parseFloat(scenario?.extraRecurringPayment || 0);
    
    // If multi-debt mode, use individual debt payments
    if (showMultiDebtMode && Object.keys(scenario?.multiDebtPayments || {})?.length > 0) {
      const optimized = calculateMultiDebtPayoffPlan(activeDebts, scenario?.multiDebtPayments, parseFloat(scenario?.oneTimePayment || 0));
      setOptimizedPlan(optimized);
    } else {
      const optimized = calculatePayoffPlan(activeDebts, totalExtraPayment, scenario?.strategy, parseFloat(scenario?.oneTimePayment || 0), scenario?.focusedDebtId);
      setOptimizedPlan(optimized);
    }

    // Calculate focused debt plan if selected
    if (scenario?.focusedDebtId) {
      const focused = calculateFocusedDebtPlan(activeDebts, scenario?.focusedDebtId, totalExtraPayment);
      setFocusedDebtPlan(focused);
    } else {
      setFocusedDebtPlan(null);
    }

    // Calculate target date analysis if set
    if (scenario?.targetPayoffDate && scenario?.focusedDebtId) {
      const targetAnalysis = calculateTargetDateRequirements(activeDebts, scenario?.focusedDebtId, scenario?.targetPayoffDate);
      setTargetDateAnalysis(targetAnalysis);
    } else {
      setTargetDateAnalysis(null);
    }

    // Generate side-by-side comparison
    const comparison = generateStrategyComparison(activeDebts, totalExtraPayment);
    setComparisonData(comparison);

    // Generate recommendation
    generateRecommendation(current, optimizedPlan || calculatePayoffPlan(activeDebts, totalExtraPayment, scenario?.strategy, parseFloat(scenario?.oneTimePayment || 0), scenario?.focusedDebtId), activeDebts);
  };

  const calculateMultiDebtPayoffPlan = (debts, multiDebtPayments, oneTimePayment = 0) => {
    // Clone debts to avoid mutation
    let debtList = debts?.map(d => ({
      ...d,
      balance: d?.currentBalance,
      monthlyPayment: d?.minimumPayment + d?.additionalFees + d?.insuranceFee,
      extraPayment: parseFloat(multiDebtPayments?.[d?.id] || 0)
    }));

    let totalMonths = 0;
    let totalInterest = 0;
    let monthlySnapshots = [];
    let debtPayoffDates = {};

    // Simulate month by month
    while (debtList?.some(d => d?.balance > 0) && totalMonths < 600) {
      totalMonths++;

      // Process each debt
      for (let debt of debtList) {
        if (debt?.balance <= 0) {
          if (!debtPayoffDates?.[debt?.id]) {
            debtPayoffDates[debt?.id] = totalMonths;
          }
          continue;
        }

        // Calculate interest for this month
        const monthlyRate = debt?.interestRate / 100 / 12;
        const interestCharge = debt?.balance * monthlyRate;
        totalInterest += interestCharge;

        // Calculate payment (minimum + extra for this specific debt)
        let payment = debt?.monthlyPayment + debt?.extraPayment;

        // Apply payment
        const principalPayment = Math.min(payment - interestCharge, debt?.balance);
        debt.balance = Math.max(0, debt?.balance - principalPayment);
      }

      monthlySnapshots?.push({
        month: totalMonths,
        totalBalance: debtList?.reduce((sum, d) => sum + d?.balance, 0)
      });
    }

    const payoffDate = new Date();
    payoffDate?.setMonth(payoffDate?.getMonth() + totalMonths);

    return {
      months: totalMonths,
      payoffDate: payoffDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      payoffDateObj: payoffDate,
      totalInterest: totalInterest,
      snapshots: monthlySnapshots,
      debtPayoffDates: debtPayoffDates
    };
  };

  const calculatePayoffPlan = (debts, extraPayment, strategy, oneTimePayment = 0, focusedDebtId = null) => {
    // Clone debts to avoid mutation
    let debtList = debts?.map(d => ({
      ...d,
      balance: d?.currentBalance,
      monthlyPayment: d?.minimumPayment + d?.additionalFees + d?.insuranceFee
    }));

    // Apply one-time payment if specified
    if (oneTimePayment > 0 && strategy !== 'current') {
      if (focusedDebtId) {
        // Apply to focused debt
        const focusedDebt = debtList?.find(d => d?.id === focusedDebtId);
        if (focusedDebt) {
          focusedDebt.balance = Math.max(0, focusedDebt?.balance - oneTimePayment);
        }
      } else if (strategy === 'avalanche') {
        // Apply to highest interest rate debt
        debtList?.sort((a, b) => b?.interestRate - a?.interestRate);
        debtList[0].balance = Math.max(0, debtList?.[0]?.balance - oneTimePayment);
      } else if (strategy === 'snowball') {
        // Apply to smallest balance debt
        debtList?.sort((a, b) => a?.balance - b?.balance);
        debtList[0].balance = Math.max(0, debtList?.[0]?.balance - oneTimePayment);
      }
    }

    let totalMonths = 0;
    let totalInterest = 0;
    let monthlySnapshots = [];
    let debtPayoffDates = {};

    // Simulate month by month
    while (debtList?.some(d => d?.balance > 0) && totalMonths < 600) {
      totalMonths++;
      let remainingExtra = extraPayment;

      // Sort debts based on strategy (only if not focusing on specific debt)
      if (!focusedDebtId) {
        if (strategy === 'avalanche') {
          debtList?.sort((a, b) => b?.interestRate - a?.interestRate);
        } else if (strategy === 'snowball') {
          debtList?.sort((a, b) => a?.balance - b?.balance);
        }
      }

      // Process each debt
      for (let debt of debtList) {
        if (debt?.balance <= 0) {
          if (!debtPayoffDates?.[debt?.id]) {
            debtPayoffDates[debt?.id] = totalMonths;
          }
          continue;
        }

        // Calculate interest for this month
        const monthlyRate = debt?.interestRate / 100 / 12;
        const interestCharge = debt?.balance * monthlyRate;
        totalInterest += interestCharge;

        // Calculate payment
        let payment = debt?.monthlyPayment;
        
        // Add extra payment logic
        if (remainingExtra > 0) {
          if (focusedDebtId) {
            // Apply all extra to focused debt
            if (debt?.id === focusedDebtId) {
              payment += remainingExtra;
              remainingExtra = 0;
            }
          } else {
            // Apply to prioritized debt (first in sorted list)
            if (debtList?.indexOf(debt) === 0) {
              payment += remainingExtra;
              remainingExtra = 0;
            }
          }
        }

        // Apply payment
        const principalPayment = Math.min(payment - interestCharge, debt?.balance);
        debt.balance = Math.max(0, debt?.balance - principalPayment);
      }

      monthlySnapshots?.push({
        month: totalMonths,
        totalBalance: debtList?.reduce((sum, d) => sum + d?.balance, 0)
      });
    }

    const payoffDate = new Date();
    payoffDate?.setMonth(payoffDate?.getMonth() + totalMonths);

    return {
      months: totalMonths,
      payoffDate: payoffDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      payoffDateObj: payoffDate,
      totalInterest: totalInterest,
      snapshots: monthlySnapshots,
      debtPayoffDates: debtPayoffDates
    };
  };

  const calculateFocusedDebtPlan = (debts, focusedDebtId, extraPayment) => {
    const focusedDebt = debts?.find(d => d?.id === focusedDebtId);
    if (!focusedDebt) return null;

    let balance = focusedDebt?.currentBalance;
    const monthlyPayment = focusedDebt?.minimumPayment + focusedDebt?.additionalFees + focusedDebt?.insuranceFee + extraPayment;
    const monthlyRate = focusedDebt?.interestRate / 100 / 12;
    
    let months = 0;
    let totalInterest = 0;

    while (balance > 0 && months < 600) {
      months++;
      const interestCharge = balance * monthlyRate;
      totalInterest += interestCharge;
      const principalPayment = Math.min(monthlyPayment - interestCharge, balance);
      balance = Math.max(0, balance - principalPayment);
    }

    const payoffDate = new Date();
    payoffDate?.setMonth(payoffDate?.getMonth() + months);

    // Calculate current plan for this debt (minimum payment only)
    let currentBalance = focusedDebt?.currentBalance;
    const currentMonthlyPayment = focusedDebt?.minimumPayment + focusedDebt?.additionalFees + focusedDebt?.insuranceFee;
    let currentMonths = 0;
    let currentInterest = 0;

    while (currentBalance > 0 && currentMonths < 600) {
      currentMonths++;
      const interestCharge = currentBalance * monthlyRate;
      currentInterest += interestCharge;
      const principalPayment = Math.min(currentMonthlyPayment - interestCharge, currentBalance);
      currentBalance = Math.max(0, currentBalance - principalPayment);
    }

    return {
      debtName: focusedDebt?.debtName,
      months: months,
      payoffDate: payoffDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      payoffDateObj: payoffDate,
      totalInterest: totalInterest,
      currentMonths: currentMonths,
      currentInterest: currentInterest,
      interestSaved: currentInterest - totalInterest,
      timeSaved: currentMonths - months
    };
  };

  const calculateTargetDateRequirements = (debts, focusedDebtId, targetDate) => {
    const focusedDebt = debts?.find(d => d?.id === focusedDebtId);
    if (!focusedDebt) return null;

    const target = new Date(targetDate);
    const today = new Date();
    const monthsToTarget = Math.max(1, Math.round((target - today) / (1000 * 60 * 60 * 24 * 30)));

    let balance = focusedDebt?.currentBalance;
    const monthlyRate = focusedDebt?.interestRate / 100 / 12;
    const minimumPayment = focusedDebt?.minimumPayment + focusedDebt?.additionalFees + focusedDebt?.insuranceFee;

    // Calculate required monthly payment using amortization formula
    // P = (r * PV) / (1 - (1 + r)^-n)
    let requiredPayment;
    if (monthlyRate === 0) {
      requiredPayment = balance / monthsToTarget;
    } else {
      requiredPayment = (monthlyRate * balance) / (1 - Math.pow(1 + monthlyRate, -monthsToTarget));
    }

    const extraRequired = Math.max(0, requiredPayment - minimumPayment);

    // Calculate interest saved by hitting target
    let currentBalance = balance;
    let currentMonths = 0;
    let currentInterest = 0;

    while (currentBalance > 0 && currentMonths < 600) {
      currentMonths++;
      const interestCharge = currentBalance * monthlyRate;
      currentInterest += interestCharge;
      const principalPayment = Math.min(minimumPayment - interestCharge, currentBalance);
      currentBalance = Math.max(0, currentBalance - principalPayment);
    }

    let targetBalance = balance;
    let targetInterest = 0;

    for (let i = 0; i < monthsToTarget; i++) {
      const interestCharge = targetBalance * monthlyRate;
      targetInterest += interestCharge;
      const principalPayment = Math.min(requiredPayment - interestCharge, targetBalance);
      targetBalance = Math.max(0, targetBalance - principalPayment);
    }

    const interestSaved = currentInterest - targetInterest;
    const isRealistic = requiredPayment < (minimumPayment * 5); // Arbitrary threshold

    return {
      debtName: focusedDebt?.debtName,
      monthsToTarget: monthsToTarget,
      requiredPayment: requiredPayment,
      extraRequired: extraRequired,
      interestSaved: interestSaved,
      isRealistic: isRealistic,
      targetDate: target?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    };
  };

  const generateStrategyComparison = (debts, extraPayment) => {
    const avalanchePlan = calculatePayoffPlan(debts, extraPayment, 'avalanche', 0);
    const snowballPlan = calculatePayoffPlan(debts, extraPayment, 'snowball', 0);
    const spreadPlan = calculatePayoffPlan(debts, extraPayment / debts?.length, 'current', 0);

    return {
      avalanche: avalanchePlan,
      snowball: snowballPlan,
      spread: spreadPlan
    };
  };

  const generateRecommendation = (current, optimized, debts) => {
    if (!current || !optimized) return;

    const interestSaved = current?.totalInterest - optimized?.totalInterest;
    const timeSaved = current?.months - optimized?.months;
    const totalExtraPayment = parseFloat(scenario?.extraMonthlyPayment || 0) + 
                             parseFloat(scenario?.extraRecurringPayment || 0);

    // Compare avalanche vs snowball
    const avalanchePlan = calculatePayoffPlan(debts?.filter(d => d?.status === 'active'), totalExtraPayment, 'avalanche', parseFloat(scenario?.oneTimePayment || 0));
    const snowballPlan = calculatePayoffPlan(debts?.filter(d => d?.status === 'active'), totalExtraPayment, 'snowball', parseFloat(scenario?.oneTimePayment || 0));

    const bestStrategy = avalanchePlan?.totalInterest < snowballPlan?.totalInterest ? 'avalanche' : 'snowball';
    const strategyDifference = Math.abs(avalanchePlan?.totalInterest - snowballPlan?.totalInterest);

    let message = '';
    let impact = 'low';

    if (scenario?.focusedDebtId && focusedDebtPlan) {
      message = `Focusing on ${focusedDebtPlan?.debtName} with an extra ${formatCurrency(totalExtraPayment)}/month pays it off in ${focusedDebtPlan?.months} months, saving ${formatCurrency(focusedDebtPlan?.interestSaved)} in interest on this debt.`;
      impact = focusedDebtPlan?.interestSaved > 500 ? 'high' : 'medium';
    } else if (totalExtraPayment > 0) {
      if (interestSaved > 1000) {
        impact = 'high';
        message = `An extra ${formatCurrency(totalExtraPayment)}/month saves you ${formatCurrency(interestSaved)} in interest and finishes ${timeSaved} months earlier. This is a highly effective strategy!`;
      } else if (interestSaved > 100) {
        impact = 'medium';
        message = `An extra ${formatCurrency(totalExtraPayment)}/month saves you ${formatCurrency(interestSaved)} in interest and finishes ${timeSaved} months earlier.`;
      } else {
        impact = 'low';
        message = `An extra ${formatCurrency(totalExtraPayment)}/month saves you ${formatCurrency(interestSaved)} in interest and finishes ${timeSaved} months earlier.`;
      }
    }

    let strategyMessage = '';
    if (strategyDifference > 100) {
      if (bestStrategy === 'avalanche') {
        strategyMessage = `Focusing on highest interest debts first saves ${formatCurrency(strategyDifference)} more in interest. This strategy is more efficient for your situation.`;
      } else {
        strategyMessage = `Paying off smallest balances first frees up cash flow sooner. This approach saves ${formatCurrency(strategyDifference)} compared to focusing on high-interest debts.`;
      }
    } else {
      strategyMessage = `Both Avalanche and Snowball strategies yield similar results (within ${formatCurrency(strategyDifference)}). Choose based on your preference.`;
    }

    setRecommendation({
      message,
      strategyMessage,
      impact,
      bestStrategy,
      interestSaved,
      timeSaved
    });
  };

  const resetScenario = () => {
    setScenario({
      extraMonthlyPayment: 0,
      oneTimePayment: 0,
      extraRecurringPayment: 0,
      strategy: 'avalanche',
      focusedDebtId: null,
      targetPayoffDate: null,
      multiDebtPayments: {}
    });
    setShowMultiDebtMode(false);
  };

  if (!isPro) {
    return (
      <div className="bg-card rounded-lg border border-border p-24 shadow-elevation-2">
        <div className="flex items-center gap-12 mb-16">
          <div className="w-48 h-48 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">What-If Analysis</h2>
            <p className="text-sm text-muted-foreground">Pro Feature</p>
          </div>
        </div>
        <div className="bg-muted/50 rounded-lg p-20 border border-border">
          <div className="flex items-start gap-12 mb-16">
            <Icon name="Lock" size={20} color="var(--color-muted-foreground)" />
            <div>
              <p className="text-foreground mb-8">
                See how focusing on one debt could save you thousands in interest.
              </p>
              <ul className="space-y-6 text-sm text-muted-foreground">
                <li className="flex items-center gap-8">
                  <Icon name="Check" size={14} color="var(--color-primary)" />
                  Focus on individual debts with targeted simulations
                </li>
                <li className="flex items-center gap-8">
                  <Icon name="Check" size={14} color="var(--color-primary)" />
                  Set target payoff dates and see required payments
                </li>
                <li className="flex items-center gap-8">
                  <Icon name="Check" size={14} color="var(--color-primary)" />
                  Compare strategies side-by-side (Avalanche vs Snowball)
                </li>
                <li className="flex items-center gap-8">
                  <Icon name="Check" size={14} color="var(--color-primary)" />
                  Get smart recommendations for maximum savings
                </li>
                <li className="flex items-center gap-8">
                  <Icon name="Check" size={14} color="var(--color-primary)" />
                  Find out exactly how much extra you need to be debt-free sooner
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const interestSaved = currentPlan && optimizedPlan ? currentPlan?.totalInterest - optimizedPlan?.totalInterest : 0;
  const timeSaved = currentPlan && optimizedPlan ? currentPlan?.months - optimizedPlan?.months : 0;
  const activeDebts = debts?.filter(d => d?.status === 'active') || [];

  return (
    <div className="bg-card rounded-lg border border-border p-24 shadow-elevation-2">
      <div className="flex items-center justify-between mb-24">
        <div className="flex items-center gap-12">
          <div className="w-48 h-48 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">What-If Analysis</h2>
            <p className="text-sm text-muted-foreground">Explore different payoff scenarios</p>
          </div>
        </div>
        <div className="flex gap-8">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowMultiDebtMode(!showMultiDebtMode)}
          >
            <Icon name="Layers" size={14} className="mr-6" />
            {showMultiDebtMode ? 'Single Focus' : 'Multi-Debt'}
          </Button>
          <Button variant="outline" size="sm" onClick={resetScenario}>
            <Icon name="RotateCcw" size={14} className="mr-6" />
            Reset
          </Button>
        </div>
      </div>

      {/* Multi-Debt Payment Mode */}
      {showMultiDebtMode && (
        <div className="bg-primary/5 rounded-lg p-20 mb-24 border border-primary/20">
          <div className="flex items-center gap-8 mb-16">
            <Icon name="Layers" size={18} color="var(--color-primary)" />
            <h3 className="font-semibold text-foreground">Apply Extra Payments to Multiple Debts</h3>
            <span className="ml-auto px-8 py-2 bg-primary/20 text-primary text-xs font-medium rounded-full">Pro Only</span>
          </div>
          <p className="text-sm text-muted-foreground mb-16">
            Assign different extra payment amounts to each debt and see how it affects your overall payoff timeline.
          </p>
          <div className="space-y-12">
            {activeDebts?.map(debt => (
              <div key={debt?.id} className="bg-background rounded-lg p-16 border border-border">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{debt?.debtName}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(debt?.currentBalance)} @ {debt?.interestRate?.toFixed(2)}% APR
                    </p>
                  </div>
                  <div className="w-32">
                    <div className="relative">
                      <span className="absolute left-8 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                      <input
                        type="number"
                        step="10"
                        min="0"
                        value={scenario?.multiDebtPayments?.[debt?.id] || 0}
                        onChange={(e) => setScenario({ 
                          ...scenario, 
                          multiDebtPayments: {
                            ...scenario?.multiDebtPayments,
                            [debt?.id]: e?.target?.value
                          }
                        })}
                        className="w-full pl-16 pr-8 py-6 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-16 p-12 bg-primary/10 rounded-lg border border-primary/20 flex items-start gap-8">
            <Icon name="Info" size={16} color="var(--color-primary)" className="mt-2 flex-shrink-0" />
            <p className="text-sm text-foreground">
              <strong>Total Extra:</strong> {formatCurrency(Object.values(scenario?.multiDebtPayments || {})?.reduce((sum, val) => sum + parseFloat(val || 0), 0))}/month across all debts
            </p>
          </div>
        </div>
      )}

      {/* Individual Debt Focus Section */}
      <div className="bg-primary/5 rounded-lg p-20 mb-24 border border-primary/20">
        <div className="flex items-center gap-8 mb-16">
          <Icon name="Target" size={18} color="var(--color-primary)" />
          <h3 className="font-semibold text-foreground">Individual Debt Focus</h3>
          <span className="ml-auto px-8 py-2 bg-primary/20 text-primary text-xs font-medium rounded-full">Pro Only</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Select Debt to Focus On
            </label>
            <select
              value={scenario?.focusedDebtId || ''}
              onChange={(e) => setScenario({ ...scenario, focusedDebtId: e?.target?.value || null })}
              className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            >
              <option value="">All Debts (Standard Strategy)</option>
              {activeDebts?.map(debt => (
                <option key={debt?.id} value={debt?.id}>
                  {debt?.debtName} - {formatCurrency(debt?.currentBalance)} @ {debt?.interestRate?.toFixed(2)}%
                </option>
              ))}
            </select>
          </div>
          {scenario?.focusedDebtId && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Target Payoff Date (Optional)
              </label>
              <input
                type="date"
                value={scenario?.targetPayoffDate || ''}
                onChange={(e) => setScenario({ ...scenario, targetPayoffDate: e?.target?.value })}
                min={new Date()?.toISOString()?.split('T')?.[0]}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>
          )}
        </div>
      </div>
      {/* Target Payoff Date Analysis */}
      {targetDateAnalysis && (
        <div className={`rounded-lg p-20 mb-24 border-2 ${
          targetDateAnalysis?.isRealistic ? 'bg-success/10 border-success' : 'bg-warning/10 border-warning'
        }`}>
          <div className="flex items-start gap-12 mb-12">
            <div className={`w-40 h-40 rounded-full flex items-center justify-center ${
              targetDateAnalysis?.isRealistic ? 'bg-success/20' : 'bg-warning/20'
            }`}>
              <Icon 
                name="Calendar" 
                size={20} 
                color={`var(--color-${targetDateAnalysis?.isRealistic ? 'success' : 'warning'})`} 
              />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-8">Target Payoff Date: {targetDateAnalysis?.targetDate}</h3>
              <p className="text-foreground mb-12">
                To pay off <strong>{targetDateAnalysis?.debtName}</strong> by {targetDateAnalysis?.targetDate} ({targetDateAnalysis?.monthsToTarget} months):
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
                <div className="bg-background/50 rounded-lg p-12">
                  <p className="text-xs text-muted-foreground mb-4">Required Monthly Payment</p>
                  <p className="text-xl font-bold text-foreground">{formatCurrency(targetDateAnalysis?.requiredPayment)}</p>
                </div>
                <div className="bg-background/50 rounded-lg p-12">
                  <p className="text-xs text-muted-foreground mb-4">Extra Needed Per Month</p>
                  <p className="text-xl font-bold text-primary">{formatCurrency(targetDateAnalysis?.extraRequired)}</p>
                </div>
                <div className="bg-background/50 rounded-lg p-12">
                  <p className="text-xs text-muted-foreground mb-4">Interest Saved</p>
                  <p className="text-xl font-bold text-success">{formatCurrency(targetDateAnalysis?.interestSaved)}</p>
                </div>
              </div>
              <div className={`flex items-center gap-8 p-12 rounded-lg ${
                targetDateAnalysis?.isRealistic ? 'bg-success/20' : 'bg-warning/20'
              }`}>
                <Icon 
                  name={targetDateAnalysis?.isRealistic ? 'CheckCircle' : 'AlertTriangle'} 
                  size={16} 
                  color={`var(--color-${targetDateAnalysis?.isRealistic ? 'success' : 'warning'})`} 
                />
                <p className="text-sm text-foreground">
                  {targetDateAnalysis?.isRealistic 
                    ? 'This goal is realistic at your current income level.' :'This goal may be challenging. Consider extending the target date or increasing income.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Scenario Controls */}
      <div className="bg-muted/30 rounded-lg p-20 mb-24">
        <div className="flex items-center gap-8 mb-16">
          <Icon name="Sliders" size={18} color="var(--color-foreground)" />
          <h3 className="font-semibold text-foreground">Adjust Your Scenario</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Extra Monthly Payment
            </label>
            <div className="relative">
              <span className="absolute left-12 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <input
                type="number"
                step="10"
                min="0"
                value={scenario?.extraMonthlyPayment}
                onChange={(e) => setScenario({ ...scenario, extraMonthlyPayment: e?.target?.value })}
                className="w-full pl-24 pr-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              One-Time Extra Payment
            </label>
            <div className="relative">
              <span className="absolute left-12 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <input
                type="number"
                step="100"
                min="0"
                value={scenario?.oneTimePayment}
                onChange={(e) => setScenario({ ...scenario, oneTimePayment: e?.target?.value })}
                className="w-full pl-24 pr-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Extra Recurring Monthly Payment
            </label>
            <div className="relative">
              <span className="absolute left-12 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <input
                type="number"
                step="10"
                min="0"
                value={scenario?.extraRecurringPayment}
                onChange={(e) => setScenario({ ...scenario, extraRecurringPayment: e?.target?.value })}
                className="w-full pl-24 pr-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                placeholder="0.00"
              />
            </div>
          </div>

          {!scenario?.focusedDebtId && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Payment Strategy
              </label>
              <select
                value={scenario?.strategy}
                onChange={(e) => setScenario({ ...scenario, strategy: e?.target?.value })}
                className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="avalanche">Avalanche (Highest Interest First)</option>
                <option value="snowball">Snowball (Smallest Balance First)</option>
              </select>
            </div>
          )}
        </div>

        <div className="mt-16 p-12 bg-primary/10 rounded-lg border border-primary/20 flex items-start gap-8">
          <Icon name="Info" size={16} color="var(--color-primary)" className="mt-2 flex-shrink-0" />
          <p className="text-sm text-foreground">
            <strong>Note:</strong> This is a simulation only. Your actual payments and debt balances will not change unless you choose to apply them.
          </p>
        </div>
      </div>
      {/* Focused Debt Results */}
      {focusedDebtPlan && (
        <div className="bg-primary/10 rounded-lg p-20 mb-24 border-2 border-primary">
          <div className="flex items-center gap-12 mb-16">
            <Icon name="Target" size={20} color="var(--color-primary)" />
            <h3 className="font-semibold text-foreground">Focused Debt Impact: {focusedDebtPlan?.debtName}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
            <div className="bg-background/50 rounded-lg p-12">
              <p className="text-xs text-muted-foreground mb-4">Payoff Time</p>
              <p className="text-lg font-bold text-foreground">{focusedDebtPlan?.months} months</p>
              <p className="text-xs text-success mt-2">{focusedDebtPlan?.timeSaved} months saved</p>
            </div>
            <div className="bg-background/50 rounded-lg p-12">
              <p className="text-xs text-muted-foreground mb-4">Payoff Date</p>
              <p className="text-lg font-bold text-foreground">{focusedDebtPlan?.payoffDate}</p>
            </div>
            <div className="bg-background/50 rounded-lg p-12">
              <p className="text-xs text-muted-foreground mb-4">Total Interest</p>
              <p className="text-lg font-bold text-destructive">{formatCurrency(focusedDebtPlan?.totalInterest)}</p>
            </div>
            <div className="bg-background/50 rounded-lg p-12">
              <p className="text-xs text-muted-foreground mb-4">Interest Saved</p>
              <p className="text-lg font-bold text-success">{formatCurrency(focusedDebtPlan?.interestSaved)}</p>
            </div>
          </div>
        </div>
      )}
      {/* Side-by-Side Strategy Comparison */}
      {comparisonData && !scenario?.focusedDebtId && (parseFloat(scenario?.extraMonthlyPayment || 0) + parseFloat(scenario?.extraRecurringPayment || 0)) > 0 && (
        <div className="bg-muted/30 rounded-lg p-20 mb-24">
          <div className="flex items-center gap-8 mb-16">
            <Icon name="GitCompare" size={18} color="var(--color-foreground)" />
            <h3 className="font-semibold text-foreground">Strategy Comparison</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Avalanche Strategy */}
            <div className="bg-card rounded-lg p-16 border border-border">
              <div className="flex items-center gap-8 mb-12">
                <Icon name="TrendingDown" size={16} color="var(--color-primary)" />
                <h4 className="font-semibold text-foreground">Avalanche Strategy</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-12">Focus on highest interest rate first</p>
              <div className="space-y-8">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Payoff Time:</span>
                  <span className="text-sm font-semibold text-foreground">{comparisonData?.avalanche?.months} months</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Interest:</span>
                  <span className="text-sm font-semibold text-destructive">{formatCurrency(comparisonData?.avalanche?.totalInterest)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Interest Saved:</span>
                  <span className="text-sm font-semibold text-success">
                    {formatCurrency(currentPlan?.totalInterest - comparisonData?.avalanche?.totalInterest)}
                  </span>
                </div>
              </div>
            </div>

            {/* Snowball Strategy */}
            <div className="bg-card rounded-lg p-16 border border-border">
              <div className="flex items-center gap-8 mb-12">
                <Icon name="Target" size={16} color="var(--color-success)" />
                <h4 className="font-semibold text-foreground">Snowball Strategy</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-12">Focus on smallest balance first</p>
              <div className="space-y-8">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Payoff Time:</span>
                  <span className="text-sm font-semibold text-foreground">{comparisonData?.snowball?.months} months</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Interest:</span>
                  <span className="text-sm font-semibold text-destructive">{formatCurrency(comparisonData?.snowball?.totalInterest)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Interest Saved:</span>
                  <span className="text-sm font-semibold text-success">
                    {formatCurrency(currentPlan?.totalInterest - comparisonData?.snowball?.totalInterest)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-16 p-12 bg-primary/10 rounded-lg border border-primary/20 flex items-center gap-8">
            <Icon name="Award" size={16} color="var(--color-primary)" />
            <p className="text-sm text-foreground">
              <strong>Most Efficient:</strong> {comparisonData?.avalanche?.totalInterest < comparisonData?.snowball?.totalInterest ? 'Avalanche' : 'Snowball'} saves {formatCurrency(Math.abs(comparisonData?.avalanche?.totalInterest - comparisonData?.snowball?.totalInterest))} more in interest
            </p>
          </div>
        </div>
      )}
      {/* Comparison Cards */}
      {currentPlan && optimizedPlan && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-24">
          {/* Current Plan */}
          <div className="bg-muted/50 rounded-lg p-20 border border-border">
            <div className="flex items-center gap-8 mb-16">
              <Icon name="Calendar" size={18} color="var(--color-muted-foreground)" />
              <h3 className="font-semibold text-foreground">Current Plan</h3>
            </div>
            <div className="space-y-12">
              <div>
                <p className="text-sm text-muted-foreground mb-4">Payoff Date</p>
                <p className="text-lg font-bold text-foreground">{currentPlan?.payoffDate}</p>
                <p className="text-xs text-muted-foreground">{currentPlan?.months} months</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-4">Total Interest Paid</p>
                <p className="text-lg font-bold text-destructive">{formatCurrency(currentPlan?.totalInterest)}</p>
              </div>
              <div className="pt-12 border-t border-border/50">
                <div className="flex items-center gap-8">
                  <div className="w-full bg-muted rounded-full h-8">
                    <div className="h-full bg-muted-foreground rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Optimized Plan */}
          <div className="bg-success/10 rounded-lg p-20 border-2 border-success">
            <div className="flex items-center gap-8 mb-16">
              <Icon name="Zap" size={18} color="var(--color-success)" />
              <h3 className="font-semibold text-foreground">What-If Scenario</h3>
            </div>
            <div className="space-y-12">
              <div>
                <p className="text-sm text-muted-foreground mb-4">Payoff Date</p>
                <p className="text-lg font-bold text-success">{optimizedPlan?.payoffDate}</p>
                <p className="text-xs text-success">{optimizedPlan?.months} months</p>
                {timeSaved > 0 && (
                  <div className="mt-6 flex items-center gap-6">
                    <Icon name="TrendingDown" size={12} color="var(--color-success)" />
                    <span className="text-xs font-medium text-success">{timeSaved} months earlier</span>
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-4">Total Interest Paid</p>
                <p className="text-lg font-bold text-success">{formatCurrency(optimizedPlan?.totalInterest)}</p>
                {interestSaved > 0 && (
                  <div className="mt-6 flex items-center gap-6">
                    <Icon name="DollarSign" size={12} color="var(--color-success)" />
                    <span className="text-xs font-medium text-success">Save {formatCurrency(interestSaved)}</span>
                  </div>
                )}
              </div>
              <div className="pt-12 border-t border-success/20">
                <div className="flex items-center gap-8">
                  <div className="w-full bg-success/20 rounded-full h-8">
                    <div 
                      className="h-full bg-success rounded-full transition-all duration-500" 
                      style={{ width: `${Math.max(10, (optimizedPlan?.months / currentPlan?.months) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Smart Recommendations */}
      {recommendation && (
        <div className={`rounded-lg p-20 border-2 ${
          recommendation?.impact === 'high' ? 'bg-success/10 border-success' :
          recommendation?.impact === 'medium'? 'bg-primary/10 border-primary' : 'bg-muted/50 border-border'
        }`}>
          <div className="flex items-start gap-12 mb-12">
            <div className={`w-40 h-40 rounded-full flex items-center justify-center ${
              recommendation?.impact === 'high' ? 'bg-success/20' :
              recommendation?.impact === 'medium'? 'bg-primary/20' : 'bg-muted'
            }`}>
              <Icon 
                name="Lightbulb" 
                size={20} 
                color={`var(--color-${
                  recommendation?.impact === 'high' ? 'success' :
                  recommendation?.impact === 'medium'? 'primary' : 'muted-foreground'
                })`} 
              />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-8">Smart Guidance</h3>
              {recommendation?.message && (
                <p className="text-foreground mb-12 leading-relaxed">
                  {recommendation?.message}
                </p>
              )}
              {recommendation?.strategyMessage && (
                <p className="text-foreground leading-relaxed">
                  {recommendation?.strategyMessage}
                </p>
              )}
            </div>
          </div>

          {/* Key Insights */}
          {(interestSaved > 0 || timeSaved > 0) && (
            <div className="mt-16 pt-16 border-t border-border/50 grid grid-cols-2 md:grid-cols-3 gap-12">
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-4">Interest Saved</p>
                <p className="text-lg font-bold text-success">{formatCurrency(interestSaved)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-4">Time Saved</p>
                <p className="text-lg font-bold text-success">{timeSaved} months</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-4">Best Strategy</p>
                <p className="text-lg font-bold text-primary capitalize">{recommendation?.bestStrategy}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save Plan Button */}
      {optimizedPlan && (scenario?.focusedDebtId || showMultiDebtMode) && (
        <div className="mt-16 flex justify-end">
          <Button onClick={() => setShowSavePlanModal(true)}>
            <Icon name="Save" size={16} className="mr-8" />
            Save This Plan
          </Button>
        </div>
      )}

      {/* Save Plan Modal */}
      {showSavePlanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
          <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-md">
            <div className="flex items-center justify-between p-24 border-b border-border">
              <h3 className="text-lg font-semibold text-card-foreground">Save Payoff Plan</h3>
              <button
                onClick={() => {
                  setShowSavePlanModal(false);
                  setPlanName('');
                  setSaveError('');
                }}
                className="p-8 hover:bg-muted rounded-lg transition-smooth"
                aria-label="Close modal"
              >
                <Icon name="X" size={20} color="var(--color-muted-foreground)" />
              </button>
            </div>

            <div className="p-24">
              {saveError && (
                <div className="mb-16 p-12 bg-destructive/10 border border-destructive rounded-lg">
                  <p className="text-sm text-destructive">{saveError}</p>
                </div>
              )}

              <div className="mb-20">
                <label className="block text-sm font-medium text-foreground mb-8">
                  Plan Name *
                </label>
                <input
                  type="text"
                  value={planName}
                  onChange={(e) => setPlanName(e?.target?.value)}
                  className="w-full px-12 py-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  placeholder="e.g., Aggressive Payoff, Balanced Approach"
                  autoFocus
                />
              </div>

              <div className="bg-muted/50 rounded-lg p-16 mb-20">
                <p className="text-sm font-medium text-foreground mb-8">Plan Summary:</p>
                <div className="space-y-6 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Strategy:</span>
                    <span className="font-semibold text-foreground capitalize">{scenario?.strategy || 'Focused'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Extra Payment:</span>
                    <span className="font-semibold text-foreground">
                      {formatCurrency(parseFloat(scenario?.extraMonthlyPayment || 0) + parseFloat(scenario?.extraRecurringPayment || 0))}/month
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Payoff Time:</span>
                    <span className="font-semibold text-foreground">{optimizedPlan?.months} months</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Interest Saved:</span>
                    <span className="font-semibold text-success">{formatCurrency(interestSaved)}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-12">
                <Button
                  type="button"
                  onClick={() => {
                    setShowSavePlanModal(false);
                    setPlanName('');
                    setSaveError('');
                  }}
                  className="flex-1 bg-muted text-foreground hover:bg-muted/80"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePlan}
                  disabled={savingPlan || !planName?.trim()}
                  className="flex-1"
                >
                  {savingPlan ? 'Saving...' : 'Save Plan'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatIfAnalysis;